"use client";

import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar";
import type React from "react";
import { cn } from "../../lib/utils";

export function Avatar({
  className,
  ...props
}: AvatarPrimitive.Root.Props): React.ReactElement {
  return (
    <AvatarPrimitive.Root
      className={cn(
        "relative inline-flex size-8 shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-background align-middle font-bold text-xs",
        // Containment ring: a 1px inset hairline of foreground ink at low
        // alpha, painted OVER the content as an ::after overlay (an <img> is a
        // replaced element, and an inset box-shadow on this root would paint
        // under it). It guarantees the silhouette when a photo's edge pixels
        // approach the surface behind it, and it is unconditional — on a solid
        // monogram fill it merely crisps the edge — so mixed photo/monogram
        // stacks stay uniform. Alpha ink composites over any surface and any
        // photo; dark mode needs a touch more to register against compressed
        // surface lightness.
        "after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-[color-mix(in_oklab,var(--foreground)_8%,transparent)] dark:after:border-[color-mix(in_oklab,var(--foreground)_12%,transparent)]",
        className,
      )}
      data-slot="avatar"
      {...props}
    />
  );
}

export function AvatarImage({
  className,
  ...props
}: AvatarPrimitive.Image.Props): React.ReactElement {
  return (
    <AvatarPrimitive.Image
      className={cn("size-full object-cover", className)}
      data-slot="avatar-image"
      {...props}
    />
  );
}

export function AvatarFallback({
  className,
  ...props
}: AvatarPrimitive.Fallback.Props): React.ReactElement {
  return (
    <AvatarPrimitive.Fallback
      className={cn(
        "flex size-full items-center justify-center rounded-full bg-muted",
        className,
      )}
      data-slot="avatar-fallback"
      {...props}
    />
  );
}
